import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { z } from "zod";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { isPaymentsConfigured } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/tracking";
import { attributionProps } from "@/lib/utm";
import { OFFERS as OFFER_REGISTRY } from "@/content/offers";
import {
  MASTERCLASS_PRICE_ID,
  trackMasterclassRegisterSubmit,
} from "@/lib/masterclass-analytics";

// Mirror of the server-side rules in
// supabase/functions/create-checkout/input.ts so guests get inline errors
// before we ever fire the create-checkout request.
const EMAIL_MAX_LENGTH = 254;
const NAME_MAX_LENGTH = 120;
const guestDetailsSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Please enter your full name (at least 2 characters)." })
    .max(NAME_MAX_LENGTH, { message: `Name must be under ${NAME_MAX_LENGTH} characters.` }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address." })
    .max(EMAIL_MAX_LENGTH, { message: `Email must be under ${EMAIL_MAX_LENGTH} characters.` }),
});
type GuestFieldErrors = Partial<Record<"name" | "email", string>>;


type Offer = {
  priceId: string;
  name: string;
  price: string;
  blurb: string;
  featured?: boolean;
  requiresAccount?: boolean;
};

const OFFERS: Offer[] = [
  {
    priceId: OFFER_REGISTRY.session.priceId!,
    name: OFFER_REGISTRY.session.name,
    price: OFFER_REGISTRY.session.priceLabel,
    blurb: "One 60-minute private strategy session with Dr. Sully. Pick your date after checkout.",
    featured: true,
  },
  {
    priceId: OFFER_REGISTRY.masterclass.priceId!,
    name: OFFER_REGISTRY.masterclass.name,
    price: OFFER_REGISTRY.masterclass.priceLabel,
    blurb: "90-minute live masterclass + lifetime replay, workbook, and 30 days in the Circle.",
  },
  {
    priceId: OFFER_REGISTRY.circle.priceId!,
    name: OFFER_REGISTRY.circle.name,
    price: `${OFFER_REGISTRY.circle.priceLabel}/mo`,
    blurb: "Monthly live call, members-only Q&A, and MAKECHANGE worksheets. Cancel anytime.",
    requiresAccount: true,
  },
  {
    priceId: OFFER_REGISTRY.keynote_deposit.priceId!,
    name: OFFER_REGISTRY.keynote_deposit.name,
    price: OFFER_REGISTRY.keynote_deposit.priceLabel,
    blurb: "Secure a keynote date. Balance invoiced against final scope of engagement.",
  },
  {
    priceId: "fsap_compassion_monthly",
    name: "Accessible FSAP Compassion Plan",
    price: "$99/mo",
    blurb:
      "Reduced-rate advocacy for SSI-level and low-income clients. Enrollment reviewed quarterly; documentation of income may be requested.",
    requiresAccount: true,
  },
  {
    priceId: "fsap_foundation_monthly",
    name: "Foundation FSAP Retainer",
    price: "$1,250/mo",
    blurb: "1 ARD/IEP meeting per month, 2 strategy calls, document review, 2-day email lane.",
    requiresAccount: true,
  },
  {
    priceId: "fsap_priority_monthly",
    name: "Priority FSAP Retainer",
    price: "$2,750/mo",
    blurb: "2 meetings per month, weekly strategy call, compliance review, priority escalation.",
    requiresAccount: true,
  },
  {
    priceId: "fsap_elite_monthly",
    name: "Elite FSAP Retainer",
    price: "$5,500/mo",
    blurb: "Unlimited local meetings, on-call access, travel-ready. Travel quoted per trip.",
    requiresAccount: true,
  },
];

const COMPASSION_PRICE_ID = "fsap_compassion_monthly";

const DRAFT_STORAGE_KEY = "drsully:book:draft:v1";

interface BookDraft {
  priceId?: string | null;
  email?: string;
  name?: string;
}

function readDraft(): BookDraft {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const d = parsed as BookDraft;
    return {
      priceId: typeof d.priceId === "string" ? d.priceId : null,
      email: typeof d.email === "string" ? d.email : "",
      name: typeof d.name === "string" ? d.name : "",
    };
  } catch {
    return {};
  }
}

function writeDraft(draft: BookDraft) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage may be full or blocked (private mode). Failing silently is
    // fine — the draft is a UX nicety, not a correctness requirement.
  }
}

export default function BookSession() {
  const [params, setParams] = useSearchParams();
  const paramPrice = params.get("price");
  // Hydrate from sessionStorage once, so a full page reload or an /auth
  // roundtrip lands the user back on the same offer with the same inputs.
  // URL `?price=` still wins if present — it's the more explicit intent.
  const initialDraft = useMemo(() => readDraft(), []);
  const [selected, setSelected] = useState<string | null>(
    paramPrice ?? initialDraft.priceId ?? null,
  );
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState(initialDraft.email ?? "");
  const [name, setName] = useState(initialDraft.name ?? "");
  const [started, setStarted] = useState(false);

  // If the draft carried a priceId but the URL didn't, reflect it in the
  // URL so refresh / share / back-button all keep working.
  useEffect(() => {
    if (!paramPrice && initialDraft.priceId) {
      setParams({ price: initialDraft.priceId }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Signed-in email always wins — but only overwrite an EMPTY field so
    // we don't stomp a different address the user deliberately typed
    // (e.g. buying a gift or using a work email for the receipt).
    if (user?.email && !email) setEmail(user.email);
  }, [user?.email, email]);

  const offer = useMemo(() => OFFERS.find((o) => o.priceId === selected) ?? null, [selected]);

  useEffect(() => {
    if (paramPrice && paramPrice !== selected) setSelected(paramPrice);
  }, [paramPrice, selected]);

  // Persist the draft whenever any of its parts change. Runs on every
  // keystroke — sessionStorage writes are cheap and the payload is tiny.
  useEffect(() => {
    writeDraft({ priceId: selected, email, name });
  }, [selected, email, name]);

  const configured = isPaymentsConfigured();

  const chooseOffer = (priceId: string) => {
    setSelected(priceId);
    setParams({ price: priceId });
    setStarted(false);
  };


  const needsAccount = !user && !!offer?.requiresAccount;

  // The Compassion Plan is a reviewed, reduced-rate enrollment: checkout only
  // unlocks after an admin approves the request for this email address.
  const isCompassion = offer?.priceId === COMPASSION_PRICE_ID;
  const [compassionApproval, setCompassionApproval] = useState<
    "idle" | "checking" | "approved" | "not_approved"
  >("idle");

  useEffect(() => {
    if (!isCompassion || !user?.email) {
      setCompassionApproval("idle");
      return;
    }
    let active = true;
    setCompassionApproval("checking");
    supabase
      .rpc("has_approved_compassion_enrollment", { _email: user.email })
      .then(({ data, error }) => {
        if (!active) return;
        setCompassionApproval(!error && data === true ? "approved" : "not_approved");
      });
    return () => {
      active = false;
    };
  }, [isCompassion, user?.email]);

  const compassionBlocked =
    isCompassion && (!user || compassionApproval !== "approved");
  // Guests must clear the same validation the edge function enforces
  // (see supabase/functions/create-checkout/input.ts) before we mount
  // Stripe. Signed-in users use their verified session email/name, so
  // the schema only gates the guest path.
  const isGuest = !user;
  const guestValidation = useMemo(
    () => (isGuest ? guestDetailsSchema.safeParse({ name, email }) : null),
    [isGuest, name, email],
  );
  const [fieldErrors, setFieldErrors] = useState<GuestFieldErrors>({});
  const [showErrors, setShowErrors] = useState(false);
  const canStart =
    !!offer &&
    !needsAccount &&
    (isGuest ? guestValidation?.success === true : email.trim().length > 3 && email.includes("@"));



  // Fire the sign-in toast once per offer selection while the visitor is
  // unauthenticated. The ref prevents a repeat toast on every rerender or
  // when the useAuth hook re-resolves the (still-null) user.
  const toastedForRef = useRef<string | null>(null);
  useEffect(() => {
    // Wait for the auth check to settle so we don't fire the toast against
    // an unresolved (initially null) user and immediately dismiss it once
    // the session hydrates.
    if (!configured || authLoading) return;
    if (offer && needsAccount) {
      if (toastedForRef.current !== offer.priceId) {
        toastedForRef.current = offer.priceId;
        toast.message("Sign in required for this subscription", {
          description: `${offer.name} is tied to your account so you can manage or cancel anytime.`,
        });

      }
    } else if (!needsAccount) {
      // Reset once the visitor signs in so a later sign-out re-arms the toast.
      toastedForRef.current = null;
    }
  }, [offer, needsAccount, configured, authLoading]);

  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <PageMeta
        path="/book"
        title="Book a Session with Dr. Sully"
        description="Reserve a private session, buy the masterclass, or join the MAKECHANGE Circle. Secure checkout."
      />
      <PaymentTestModeBanner />
      <SiteNav />
      <main id="main" className="container max-w-5xl py-16 md:py-24">
        <div className="text-center mb-12">
          <span className="eyebrow inline-flex">Choose your next move</span>
          <h1 className="font-display font-bold uppercase text-3xl md:text-5xl mt-4">
            One step closer to <span className="text-gradient-gold">the change.</span>
          </h1>
          <p className="text-silver mt-4 max-w-2xl mx-auto">
            Most leaders start with the <span className="text-white font-semibold">$47/mo Circle</span> —
            monthly coaching, worksheets, and Q&amp;A with Dr. Sully. Prefer 1:1? Book a private session below.
            Secure inline checkout — cancel anytime.
          </p>
        </div>


        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {OFFERS.map((o) => (
            <button
              key={o.priceId}
              onClick={() => chooseOffer(o.priceId)}
              className={cn(
                "text-left rounded-xl border p-5 transition-all",
                selected === o.priceId
                  ? "border-gold bg-gold/5 shadow-cta"
                  : "border-border hover:border-gold/60 bg-navy/40",
                o.featured && selected !== o.priceId && "ring-1 ring-gold/40",
              )}
            >
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-display uppercase tracking-wide text-white">{o.name}</h3>
                <span className="font-display text-gold text-lg">{o.price}</span>
              </div>
              <p className="text-silver text-sm mt-2 leading-relaxed">{o.blurb}</p>
              <div className="mt-3">
                {o.requiresAccount ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-[11px] font-medium text-gold uppercase tracking-wider">
                    <Lock className="h-3 w-3" aria-hidden="true" />
                    Sign-in required · subscription
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-medium text-emerald-300 uppercase tracking-wider">
                    Guest checkout · no account needed
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>


        {!configured && (
          <div className="rounded-lg border border-red-400/40 bg-red-500/10 text-red-100 p-5 text-sm">
            Payments aren't fully configured yet. Please try again shortly — or{" "}
            <Link to="/inquire" className="underline">apply directly</Link>.
          </div>
        )}

        {configured && offer && authLoading && (
          // Reserve the same footprint as the sign-in / details cards below
          // so the auth check doesn't cause a layout jump when it resolves.
          <div
            role="status"
            aria-live="polite"
            aria-label="Checking your sign-in status"
            className="rounded-xl border border-border bg-navy/40 p-6 md:p-8 max-w-xl mx-auto"
          >
            <Skeleton className="mx-auto mb-4 h-12 w-12 rounded-full bg-white/5" />
            <Skeleton className="mx-auto mb-3 h-5 w-2/3 bg-white/5" />
            <Skeleton className="mx-auto mb-2 h-4 w-11/12 bg-white/5" />
            <Skeleton className="mx-auto mb-6 h-4 w-9/12 bg-white/5" />
            <Skeleton className="mx-auto h-12 w-56 rounded-md bg-white/5" />
            <span className="sr-only">Checking your sign-in status…</span>
          </div>
        )}

        {configured && offer && !authLoading && needsAccount && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-xl border border-gold/40 bg-gold/5 p-6 md:p-8 max-w-xl mx-auto text-center"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gold/15">
              <Lock className="h-5 w-5 text-gold" aria-hidden="true" />
            </div>
            <h2 className="font-display uppercase text-xl mb-2">Sign in required for this subscription</h2>
            <p className="text-silver text-sm mb-2">
              <span className="text-white font-semibold">{offer.name}</span> —{" "}
              <span className="text-white font-semibold">{offer.price}</span> is a recurring
              membership. An account lets you:
            </p>
            <ul className="text-silver text-sm mb-5 space-y-1 max-w-sm mx-auto text-left list-disc list-inside">
              <li>Manage or cancel your subscription anytime.</li>
              <li>Access members-only calls, replays, and worksheets.</li>
              <li>Keep a secure record of every renewal receipt.</li>
            </ul>
            <Link
              to={`/auth?next=${encodeURIComponent(`/book?price=${offer.priceId}`)}`}
              className="inline-block bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-6 py-4 rounded-md shadow-cta hover:brightness-110 transition"
            >
              Sign in / create account →
            </Link>
            <p className="text-xs text-silver/60 mt-3">
              We'll bring you right back here after you sign in — your selection is saved. One-time
              offers don't require an account.
            </p>
          </div>
        )}



        {configured && offer && !authLoading && !needsAccount && compassionBlocked && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-xl border border-gold/40 bg-gold/5 p-6 md:p-8 max-w-xl mx-auto text-center"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gold/15">
              <Lock className="h-5 w-5 text-gold" aria-hidden="true" />
            </div>
            <h2 className="font-display uppercase text-xl mb-2">Approval required</h2>
            {compassionApproval === "checking" ? (
              <p className="text-silver text-sm">Checking your enrollment status…</p>
            ) : (
              <>
                <p className="text-silver text-sm mb-5">
                  The Accessible FSAP Compassion Plan ($99–$129/mo) is a reviewed, reduced-rate
                  enrollment. Submit a short request first — once it's approved, this checkout
                  unlocks at your approved monthly rate. Nothing is charged before approval.
                </p>
                <Link
                  to="/advocacy/compassion-apply"
                  className="inline-block bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-6 py-4 rounded-md shadow-cta hover:brightness-110 transition"
                >
                  Apply for the Compassion Plan →
                </Link>
                <p className="text-xs text-silver/60 mt-3">
                  Already applied and approved? Sign in with the email you applied with — approvals
                  are matched to that address.
                </p>
              </>
            )}
          </div>
        )}

        {configured && offer && !authLoading && !needsAccount && !compassionBlocked && !started && (
          <div className="rounded-xl border border-border bg-navy/40 p-6 md:p-8 max-w-xl mx-auto">
            <h2 className="font-display uppercase text-xl mb-1">Your details</h2>
            <p className="text-xs text-silver/70 mb-4">
              {user
                ? `Signed in as ${user.email}. Your receipt will be sent to this address — update your account email to change it.`
                : "No account required — checkout as a guest. We'll email your receipt."}
            </p>
            <div className="space-y-3">
              <div>
                <Input
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: undefined }));
                  }}
                  onBlur={() => {
                    if (!isGuest) return;
                    const r = guestDetailsSchema.shape.name.safeParse(name);
                    setFieldErrors((p) => ({
                      ...p,
                      name: r.success ? undefined : r.error.issues[0]?.message,
                    }));
                  }}
                  aria-invalid={showErrors && !!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? "book-name-error" : undefined}
                  maxLength={NAME_MAX_LENGTH}
                  className={cn(
                    "bg-navy-deep border-border text-white",
                    showErrors && fieldErrors.name && "border-red-400 focus-visible:ring-red-400",
                  )}
                />
                {showErrors && fieldErrors.name && (
                  <p id="book-name-error" role="alert" className="mt-1 text-xs text-red-300">
                    {fieldErrors.name}
                  </p>
                )}
              </div>
              <div>
                <Input
                  type="email"
                  required
                  placeholder="Email for receipt & confirmation"
                  value={email}
                  readOnly={!!user}
                  aria-readonly={!!user}
                  title={
                    user
                      ? "Receipt is sent to your signed-in account email. Update your account email to change it."
                      : undefined
                  }
                  onChange={(e) => {
                    if (user) return;
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: undefined }));
                  }}
                  onBlur={() => {
                    if (!isGuest) return;
                    const r = guestDetailsSchema.shape.email.safeParse(email);
                    setFieldErrors((p) => ({
                      ...p,
                      email: r.success ? undefined : r.error.issues[0]?.message,
                    }));
                  }}
                  aria-invalid={showErrors && !!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? "book-email-error" : undefined}
                  maxLength={EMAIL_MAX_LENGTH}
                  className={cn(
                    "bg-navy-deep border-border text-white",
                    user && "opacity-70 cursor-not-allowed",
                    showErrors && fieldErrors.email && "border-red-400 focus-visible:ring-red-400",
                  )}
                />
                {showErrors && fieldErrors.email && (
                  <p id="book-email-error" role="alert" className="mt-1 text-xs text-red-300">
                    {fieldErrors.email}
                  </p>
                )}
              </div>
              <Button
                onClick={() => {
                  if (isGuest) {
                    const result = guestDetailsSchema.safeParse({ name, email });
                    if (!result.success) {
                      const errs: GuestFieldErrors = {};
                      for (const issue of result.error.issues) {
                        const key = issue.path[0] as "name" | "email" | undefined;
                        if (key && !errs[key]) errs[key] = issue.message;
                      }
                      setFieldErrors(errs);
                      setShowErrors(true);
                      return;
                    }
                  }
                  setFieldErrors({});
                  setShowErrors(false);
                  setStarted(true);
                  trackEvent("checkout_details_submit", {
                    price_id: offer.priceId,
                    offer_name: offer.name,
                    auth_state: user ? "signed_in" : "guest",
                    ...attributionProps(),
                  });
                  if (offer.priceId === MASTERCLASS_PRICE_ID) {
                    trackMasterclassRegisterSubmit({
                      auth_state: user ? "signed_in" : "guest",
                    });
                  }
                }}
                className="w-full bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] text-sm py-6 shadow-cta disabled:opacity-60"
                disabled={isGuest && !canStart && showErrors}
              >
                {user ? "Continue to secure checkout →" : "Continue as guest to secure checkout →"}
              </Button>
              <p className="text-xs text-silver/70 text-center">
                A receipt is emailed automatically after successful payment.
              </p>
            </div>

          </div>
        )}


        {configured && offer && started && !compassionBlocked && (
          <div className="rounded-xl border border-border bg-white p-2 md:p-4">
            <StripeEmbeddedCheckout
              priceId={offer.priceId}
              customerEmail={email}
              customerName={name || undefined}
              userId={user?.id}
            />
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
